// Data table on TanStack Table with server cursor pagination ("Cargar más").
// Sorting/filtering stay server-side; the table only renders rows.
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { Button } from "@/ui/button";
import { EmptyState, Skeleton, Table, Td, Th } from "@/ui/layout";

export function DataTable<T>({
  columns,
  rows,
  loading,
  empty,
  hasMore,
  loadingMore,
  onLoadMore,
  label,
}: {
  columns: Array<ColumnDef<T>>;
  rows: T[];
  loading: boolean;
  empty: ReactNode;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  label: string;
}) {
  // TanStack Table v8 returns mutable helpers the React Compiler cannot memoize; this component is
  // not compiler-optimized (documented incompatibility, behavior unaffected).
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() });
  if (loading) {
    return (
      <div className="grid gap-2" aria-busy="true" aria-label={`Cargando ${label}`}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-9" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) return <>{empty}</>;
  return (
    <div className="grid gap-2">
      <Table aria-label={label}>
        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((header) => (
                <Th key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </Th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="hover:bg-sunken/60">
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
      {hasMore ? (
        <div>
          <Button size="sm" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? "Cargando…" : "Cargar más"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export { EmptyState };
